import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./OpenOrders.styles.scss";

// Type Imports
import { IAlertMessage } from "@pulse-ui/types/components/AlertMessage";
import { NormalizedOpenOrderRow } from "./OpenOrders.types";
import { IAGOpenOrdersDataRow } from "@pulse-ui/types/components/AGTable";
import {
  IExecutionReport,
  IExecutionReportMiscFeesGrp,
  IOrderRestatement,
} from "@pulse-ui/codegen/generated_ts/drop_copy";
import { OrderStatus } from "@pulse-ui/codegen/generated_ts/types";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { ContextStateType } from "../../context/context.types";

// Component Imports
import { AlertMessage } from "../../components";
import { DropdownInput } from "../../components/Inputs";
import { Loading } from "../../layout";
import SocketAGTable from "../../components/SocketAGTable/SocketAGTable";

// Context Imports
import { useAppContext } from "../../context/context";

// Material UI Imports
import { SvgIcon } from "@mui/material";

// Package Imports
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Utils/Formatting Functions
import {
  getOrgMetadata,
  getOrgs,
  setAccountById,
  setOrgMetadata,
  setPortfolioById,
} from "../../utils/promises";
import { usePortfolioAndAccountSelection } from "../../utils/hooks/usePortfolioAndAccountSelection/usePortfolioAndAccountSelection";
import { grab_base_url } from "../../utils/api_methods";
import { formatIsoDateToLocale } from "@pulse-ui/formatting";
import { useAlert } from "@/utils/hooks/useAlert/useAlert";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

// AG Grid Imports
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import {
  currencyFormatter,
  customNumericSortFunc,
  numericFormatterNoPrecision,
} from "../../utils/helper_functions";
import VenueCell from "../../components/AGCellRenderers/VenueCell";
import OrderStatusCell from "../../components/AGCellRenderers/OrderStatusCell";

const OpenOrders = () => {
  // State - START -------------------------------------
  // Query Params States
  const [queryParams, setQueryParams] = useSearchParams({
    accounts: "",
  });
  const location_origin = window.location.origin;
  const accounts_param = queryParams.get("accounts");
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [readyForSocket, setReadyForSocket] = useState<boolean>(false);

  // Error Handling / Alert Message States
  const { alerts, pushAlert, closeAlert, clearAlerts } = useAlert();

  // Context States
  const {
    context_state,
    update_org_metadata,
    set_portfolio_by_id,
    set_account_by_id,
  } = useAppContext();

  // Portfolio/Account Selection States
  const stringified_local_metadata: string | null =
    localStorage.getItem("pulse-ctx");
  const local_metadata: ContextStateType | null = stringified_local_metadata
    ? JSON.parse(stringified_local_metadata)
    : null;

  const [dataMap, setDataMap] = useState<Map<string, NormalizedOpenOrderRow>>(
    new Map([])
  );
  const socketRef = useRef<WebSocket | null>(null);
  const dataMapRef = useRef(dataMap);
  const gridRef = useRef<AgGridReact<IAGOpenOrdersDataRow> | null>(null);
  const [liveTimestamp, setLiveTimestamp] = useState<string | null>(null);
  const [initialRender, setInitialRender] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);

  // State - END ---------------------------------------`

  // Methods - START -----------------------------------
  const handleNavigate = (account_id: string) => {
    setQueryParams((prev: URLSearchParams) => {
      prev.set("accounts", account_id);
      return prev;
    });
  };

  const mapIPortfoliosToSelectOptions = (
    portfolios: IPortfolioMetadata[]
  ): ISelectOption[] => {
    if (!portfolios) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let portfolio of portfolios) {
        options.push({
          label: portfolio.display_name,
          value: portfolio.portfolio_id,
        });
      }

      return options;
    }
  };

  const mapIAccountsToSelectOptions = (
    accounts: IAccountMetadata[]
  ): ISelectOption[] => {
    if (!accounts) {
      return [];
    } else {
      let options: ISelectOption[] = [];

      for (let account of accounts) {
        options.push({
          label: account.display_name,
          value: account.account_id,
        });
      }

      return options;
    }
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (alerts.length > 0) clearAlerts();

    if (name === "portfolio-select") {
      value === "" ? set_portfolio_by_id(null) : set_portfolio_by_id(value);
      set_account_by_id(null);
      return;
    }

    if (name === "account-select") {
      value === "" ? set_account_by_id(null) : set_account_by_id(value);
    }
  };

  const handleInitialDataLoad = async () => {
    setLoading(true);

    try {
      const get_orgs_res = await getOrgs(location_origin);
      const get_org_metadata_res = await getOrgMetadata(
        get_orgs_res.data.org_id,
        location_origin
      );
      await setOrgMetadata(get_org_metadata_res.data, update_org_metadata);

      const { portfolio_id_res, account_id_res } =
        await usePortfolioAndAccountSelection(
          accounts_param,
          local_metadata,
          get_org_metadata_res.data,
          true
        );

      handleNavigate(account_id_res || "");

      await setPortfolioById(portfolio_id_res || null, set_portfolio_by_id);
      await setAccountById(account_id_res, set_account_by_id);

      let accounts: string | null = account_id_res;

      if (portfolio_id_res) {
        if (accounts) {
          setReadyForSocket(true);
        }
      } else {
        set_account_by_id(null);
        set_portfolio_by_id(null);
        navigate("/unauthorized", { replace: true });
      }
    } catch (error_res: any) {
      if (error_res.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: 408,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message: error_res.message,
          status: error_res?.status || 500,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const setDataMapRef = (newDataMap: Map<any, any>) => {
    dataMapRef.current = newDataMap;
    setDataMap(newDataMap);
  };

  const isOrderRestatement = (
    row: IOrderRestatement | IExecutionReport
  ): row is IOrderRestatement => {
    return (row as IOrderRestatement).orders_snapshot !== undefined;
  };

  const getMiscFees = (
    fees: IExecutionReportMiscFeesGrp[] | null
  ): {
    exchange_fees: IExecutionReportMiscFeesGrp | null;
    tax: IExecutionReportMiscFeesGrp | null;
    local_commission: IExecutionReportMiscFeesGrp | null;
  } => {
    let misc_fees_group: any = {};

    if (fees && fees.length > 0) {
      for (let fee of fees) {
        misc_fees_group[fee.misc_fee_type] = {
          misc_fee_amt: fee.misc_fee_amt,
          misc_fee_curr: fee.misc_fee_curr,
          misc_fee_type: fee.misc_fee_type,
        };
      }
    }

    return misc_fees_group;
  };

  const parseTableData = (
    rpt: IExecutionReport,
    parsedEvent: IOrderRestatement | IExecutionReport
  ): NormalizedOpenOrderRow => {
    const misc_fees_group_res = getMiscFees(rpt.misc_fees_grp);

    return {
      row_id: `${rpt.order_id}-${rpt.client_order_id}`,
      client_order_id: rpt.client_order_id,
      order_id: rpt.order_id,
      exec_id: rpt.exec_id,
      venue: rpt.venue || "-",
      symbol: rpt.symbol,
      price: rpt.price?.toLocaleString() || "-",
      order_qty: rpt.order_qty?.toLocaleString() || "-",
      cum_qty: rpt.cum_qty.toLocaleString(),
      leaves_qty: rpt.leaves_qty.toLocaleString(),
      avg_px: rpt.avg_px?.toLocaleString() || "-",
      transact_time: parsedEvent?.transact_time
        ? parsedEvent.transact_time
        : rpt?.transact_time
        ? rpt.transact_time
        : "-",
      exec_type: rpt.exec_type.toString().toUpperCase(),
      ord_type: rpt.ord_type?.toString().toUpperCase() || "-",
      placement_strategy:
        rpt.placement_strategy?.toString().split("_").join(" ").toUpperCase() ||
        "-",
      side: rpt.side?.toString().toUpperCase() || "-",
      status: rpt.status.toString().toUpperCase(),
      exchange_misc_fee_amt:
        misc_fees_group_res && misc_fees_group_res?.exchange_fees
          ? misc_fees_group_res.exchange_fees.misc_fee_amt.toString()
          : "-",
      exchange_misc_fee_curr:
        misc_fees_group_res && misc_fees_group_res?.exchange_fees
          ? misc_fees_group_res.exchange_fees.misc_fee_curr
          : "-",
      tax_misc_fee_amt:
        misc_fees_group_res && misc_fees_group_res?.tax
          ? misc_fees_group_res.tax.misc_fee_amt.toString()
          : "-",
      tax_misc_fee_curr:
        misc_fees_group_res && misc_fees_group_res?.tax
          ? misc_fees_group_res.tax.misc_fee_curr
          : "-",
      local_comm_misc_fee_amt: "-",
      local_comm_misc_fee_curr: "-",
      reason: isOrderRestatement(parsedEvent)
        ? parsedEvent.reason.toString()
        : "-",
    };
  };

  const connectToWebsocket = (
    accounts: string
  ): Promise<{
    status: number;
    error: any;
  }> => {
    return new Promise((resolve, reject) => {
      const jwtToken = localStorage.getItem("pulsejwt");

      const websocket_url = `${grab_base_url(
        location_origin,
        "wss"
      )}/api/dropcopy/v1/order_event?accounts=${accounts}`;

      if (jwtToken) {
        if (socketRef.current === null) {
          socketRef.current = new WebSocket(websocket_url, [jwtToken]);

          let messageReceived = false;
          let messageTimeout: any;

          const connectionTimeout = setTimeout(() => {
            socketRef.current?.close();
            socketRef.current = null;
            reject({
              status: 408,
              error: "Connection timed out",
            });
          }, 5000);

          socketRef.current.onopen = () => {
            clearTimeout(connectionTimeout);

            messageTimeout = setTimeout(() => {
              if (!messageReceived) {
                socketRef.current?.close();
                socketRef.current = null;
                reject({
                  status: 408,
                  data: null,
                  error: "No messages received",
                });
              }
            }, 5000);
          };

          socketRef.current.onmessage = (event: any): void => {
            const parsedEvent: IOrderRestatement | IExecutionReport =
              JSON.parse(event.data);

            messageReceived = true;
            clearTimeout(messageTimeout);

            if (isOrderRestatement(parsedEvent)) {
              const new_orders_snapshot: IExecutionReport[] =
                parsedEvent.orders_snapshot;

              setLiveTimestamp(parsedEvent.transact_time);

              if (new_orders_snapshot.length > 0) {
                const map = new Map<string, NormalizedOpenOrderRow>(
                  new_orders_snapshot
                    .sort((a: any, b: any) => {
                      if (a.symbol < b.symbol) return -1;
                      if (a.symbol > b.symbol) return 1;
                      return 0;
                    })
                    .map((rpt: IExecutionReport) => {
                      return [
                        `${rpt.order_id}-${rpt.client_order_id}`,
                        parseTableData(rpt, parsedEvent),
                      ];
                    })
                );

                setDataMapRef(map);
              } else {
                setDataMapRef(new Map([]));
              }

              resolve({
                status: 200,
                error: null,
              });
            } else {
              const statusKey =
                parsedEvent.status as unknown as keyof typeof OrderStatus;
              let temp_map = new Map(dataMapRef.current);
              if (
                OrderStatus[statusKey] === OrderStatus.canceled ||
                OrderStatus[statusKey] === OrderStatus.expired ||
                OrderStatus[statusKey] === OrderStatus.filled ||
                OrderStatus[statusKey] === OrderStatus.rejected
              ) {
                temp_map.delete(
                  `${parsedEvent.order_id}-${parsedEvent.client_order_id}`
                );
              } else {
                temp_map.set(
                  `${parsedEvent.order_id}-${parsedEvent.client_order_id}`,
                  parseTableData(parsedEvent, parsedEvent)
                );
              }

              setDataMapRef(temp_map);

              setLiveTimestamp(parsedEvent.transact_time);
              return;
            }
          };

          socketRef.current.onerror = (event: any) => {
            reject({
              status: event.code,
              data: null,
              error: event,
            });
          };
        }
      } else {
        reject({
          status: 401,
          data: null,
          error:
            "Your session has expired. If you're not automatically redirected, please navigate to the home page.",
        });
      }
    });
  };

  const disconnectFromWebsocket = () => {
    if (socketRef.current) {
      if (
        socketRef.current.readyState === 1 ||
        socketRef.current.readyState === 3
      ) {
        socketRef.current.close();
        socketRef.current = null;
      }
    }
  };

  const handleSocket = async (accounts: string) => {
    try {
      await connectToWebsocket(accounts);
    } catch (socket_connection_error: any) {
      if (socket_connection_error.status === 408) {
        pushAlert({
          id: uuidv4(),
          message: socket_connection_error.message,
          status: 408,
          onClickText: "Reload page?",
          onClickMethod: () => window.location.reload(),
        });
      } else {
        pushAlert({
          id: uuidv4(),
          message:
            socket_connection_error.message || "Socket connection failed.",
          status: socket_connection_error?.status || 500,
        });
      }
    } finally {
      if (initialRender) {
        setInitialRender(false);
      }

      setTableLoading(false);
    }
  };
  // Methods - END -------------------------------------

  // Table Configurations - START ----------------------
  const colDefs = useMemo<ColDef<IAGOpenOrdersDataRow>[]>(
    () => [
      {
        headerName: "Client Order ID",
        field: "client_order_id",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Venue",
        field: "venue",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: VenueCell,
      },
      {
        headerName: "Status",
        field: "status",
        cellClass: "ag-center-aligned-cell",
        cellRenderer: OrderStatusCell,
        cellRendererParams: (params: any) => ({
          cum_qty: params.data.cum_qty,
          order_qty: params.data.order_qty,
        }),
      },
      {
        headerName: "Symbol",
        field: "symbol",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "$Price",
        field: "price",
        valueFormatter: (params: any) => currencyFormatter(params, "$"),
        comparator: customNumericSortFunc,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Order Qty",
        field: "order_qty",
        valueFormatter: numericFormatterNoPrecision,
        comparator: customNumericSortFunc,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Cummulative Qty",
        field: "cum_qty",
        valueFormatter: numericFormatterNoPrecision,
        comparator: customNumericSortFunc,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Leaves Qty",
        field: "leaves_qty",
        valueFormatter: numericFormatterNoPrecision,
        comparator: customNumericSortFunc,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "$Average Price",
        field: "avg_px",
        valueFormatter: (params: any) => currencyFormatter(params, "$"),
        comparator: customNumericSortFunc,
        cellClass: "ag-right-aligned-cell",
      },
      {
        headerName: "Side",
        field: "side",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Transact Time",
        field: "transact_time",
        cellClass: "ag-center-aligned-cell",
        valueFormatter: (params: any) => formatIsoDateToLocale(params.value),
      },
      {
        headerName: "Exec Type",
        field: "exec_type",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Order Type",
        field: "ord_type",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Placement Strategy",
        field: "placement_strategy",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Exchange Misc. Fee Amount",
        field: "exchange_misc_fee_amt",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Exchange Misc. Fee Currency",
        field: "exchange_misc_fee_curr",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Tax Misc. Fee Amount",
        field: "tax_misc_fee_amt",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Tax Misc. Fee Currency",
        field: "tax_misc_fee_curr",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Local Commission Misc. Fee Amount",
        field: "local_comm_misc_fee_amt",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Local Commission Misc. Fee Currency",
        field: "local_comm_misc_fee_curr",
        cellClass: "ag-left-aligned-cell",
      },
      {
        headerName: "Order ID",
        field: "order_id",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Reason",
        field: "reason",
        cellClass: "ag-center-aligned-cell",
      },
      {
        headerName: "Exec ID",
        field: "exec_id",
        cellClass: "ag-center-aligned-cell",
      },
    ],
    []
  );

  const defaultColDef: ColDef = {
    filter: true,
  };
  // Table Configurations - END ------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    handleInitialDataLoad();

    return () => {
      disconnectFromWebsocket();
    };
  }, []);

  useEffect(() => {
    if (readyForSocket && context_state.selected_account?.account_id) {
      handleSocket(context_state.selected_account.account_id);
    }
  }, [readyForSocket]);

  useEffect(() => {
    if (context_state?.selected_account) {
      setTableLoading(true);
    }

    setDataMapRef(new Map([]));
    disconnectFromWebsocket();

    if (context_state?.selected_account && !initialRender) {
      handleNavigate(context_state.selected_account.account_id);
      handleSocket(context_state.selected_account.account_id);
    } else {
      handleNavigate("");
    }
  }, [context_state.selected_account]);
  // useEffects - END ----------------------------------

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      {alerts.map((alert: IAlertMessage) => {
        return (
          <div className="pulse-mb-1" style={{ width: "100%" }} key={uuidv4()}>
            <AlertMessage
              id={alert.id}
              message={alert.message}
              status={alert.status}
              onClickText={alert.onClickText}
              onClickMethod={alert.onClickMethod}
              closeAlertMessage={closeAlert}
            />
          </div>
        );
      })}

      <div className="pulse-row open-orders-header-row open-orders-position-relative pulse-mb-2">
        <p className="pulse-typography-headline-6 pulse-color--background-50">
          Open Orders
        </p>
        <div className="balances-dropdown-inputs-container">
          <DropdownInput
            name="portfolio-select"
            value={context_state.selected_portfolio?.portfolio_id || ""}
            label="Portfolio"
            onChange={handleDropdownChange}
            disabled={context_state.org_metadata.portfolios.length < 1}
            options={mapIPortfoliosToSelectOptions(
              context_state.org_metadata.portfolios
            )}
            formInput={false}
            containerClassNames=""
          />

          <DropdownInput
            name="account-select"
            value={context_state.selected_account?.account_id || ""}
            label="Account"
            onChange={handleDropdownChange}
            disabled={
              context_state.selected_portfolio?.accounts
                ? context_state.selected_portfolio?.accounts.length < 1
                  ? true
                  : false
                : true
            }
            options={mapIAccountsToSelectOptions(
              context_state.selected_portfolio?.accounts || []
            )}
            formInput={false}
            containerClassNames=""
          />
        </div>
      </div>

      <div className="pulse-row pulse-mb-1">
        <div className="pulse-color--background-800 pulse-justify-flex-end pulse-align-flex-end pulse-col-md-12 pulse-col-lg-12 pulse-col-xl-12">
          <p
            className="pulse-typography-subtitle-2"
            style={{ color: "#b6bdce" }}
          >
            {`Updated: ${dayjs(liveTimestamp)
              .utc()
              .format("ddd, D MMM YYYY HH:mm:ss")} UTC`}
          </p>
        </div>
      </div>

      <SocketAGTable
        gridRef={gridRef}
        rowData={[...dataMapRef.current.values()]}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        loading={tableLoading}
      />

      {loading && <Loading />}
    </div>
  );
};

export default OpenOrders;
